import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { reportSettingsContract } from "@common/contracts/report-settings.contract";
import { WorkspaceReportSettingsResponse } from "@common/schemas/domains/reportSettings/workspace-report-settings.schema";
import { SaveWorkspaceReportSettingsBody } from "@common/schemas/domains/reportSettings/save-report-settings.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const reportSettingsClient = getBackendAPIClient(reportSettingsContract);

export const reportSettingsQueryKeys = {
    allWorkspaces: () => ["report-settings", "workspace"] as const,
    workspace: (workspaceId: string) =>
        ["report-settings", "workspace", workspaceId] as const,
};

export function useWorkspaceReportSettings(
    workspaceId: string,
    options?: { enabled?: boolean },
) {
    return useQuery({
        queryKey: reportSettingsQueryKeys.workspace(workspaceId),
        queryFn: async (): Promise<WorkspaceReportSettingsResponse> => {
            const response =
                await reportSettingsClient.getWorkspaceReportSettings({
                    params: { workspaceId },
                });
            if (response.status !== 200) {
                throw new Error("Failed to load report settings");
            }
            return response.body;
        },
        refetchOnWindowFocus: false,
        enabled: options?.enabled ?? true,
    });
}

export function useSaveWorkspaceReportSettingsMutation(workspaceId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            body: SaveWorkspaceReportSettingsBody,
        ): Promise<void> => {
            const response =
                await reportSettingsClient.updateWorkspaceReportSettings({
                    params: { workspaceId },
                    body,
                });
            if (response.status !== 200) {
                throw new Error("Failed to save report settings");
            }
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: reportSettingsQueryKeys.workspace(workspaceId),
            });
        },
    });
}
